import { create } from "zustand";
import { PLAIN_TEMPLATE_OBJECT } from "../../constants/AppConstants";

export const useTemplateStore = create((set, get) => ({
  allTemplates: [],
  templates: [],
  templateLoading: false,
  fetched: false,
  lastFetchedModule: null,
  lastFetchedIntegrationId: null,

  fetchTemplates: async ({
    refresh = false,
    templatesSelectOpen = false,
    appProperties,
    moduleName,
    fetchOnlyByModule = false,
    integrationId,
    fetchSavedTemplatesByModule,
    fetchSavedTemplates,
    fetchAllTemplates = false,
  }) => {
    if (!appProperties?.osyncId) return;

    const { templateLoading, fetched, allTemplates, lastFetchedIntegrationId, lastFetchedModule } = get();

    // Prevent concurrent API calls - check loading state first
    if (templateLoading || !integrationId) return;

    // If already fetched and not refreshing, check if we can use cached data
    if (fetched && !refresh) {
      const moduleToFetch = moduleName || (await appProperties?.controller?.getAssociatedObjectType(appProperties));
      // If same integrationId and module, no need to refetch or refilter
      if (lastFetchedIntegrationId === integrationId && lastFetchedModule === moduleToFetch) {
        return;
      }

      // Filter from existing cache if module/integrationId changed
      const filteredTemplates = filterByServiceByModule(appProperties, integrationId, allTemplates, moduleToFetch, fetchAllTemplates);

      set({
        templates: filteredTemplates,
        lastFetchedIntegrationId: integrationId,
        lastFetchedModule: moduleToFetch,
      });
      return;
    }

    // Only fetch if templatesSelectOpen is true or if we're refreshing
    if (!templatesSelectOpen && !refresh) {
      return;
    }

    // Set loading state immediately to prevent race conditions
    // This must be set synchronously before any async operations
    set({ templateLoading: true });

    try {
      const moduleToFetch = moduleName || (await appProperties?.controller?.getAssociatedObjectType(appProperties));
      let templatesData = [];

      // When fetchOnlyByModule is true → fetch by module (backend will include general templates)
      if (fetchOnlyByModule && moduleToFetch) {
        const res = await fetchSavedTemplatesByModule(appProperties, moduleToFetch);
        templatesData = res?.data || [];
      } else {
        // When fetchOnlyByModule is false → fetch all templates
        const res = await fetchSavedTemplates(appProperties);
        templatesData = res?.data || [];
      }

      // Filter templates based on current request
      const filteredTemplates = filterByServiceByModule(appProperties, integrationId, templatesData, moduleToFetch, fetchAllTemplates);

      set({
        allTemplates: templatesData,
        templates: filteredTemplates,
        fetched: true,
        lastFetchedIntegrationId: integrationId,
        lastFetchedModule: moduleToFetch,
      });
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      set({ templateLoading: false });
    }
  },

  clearStore: () => {
    set({
      allTemplates: [],
      templates: [],
      templateLoading: false,
      fetched: false,
      lastFetchedModule: null,
      lastFetchedIntegrationId: null,
    });
  },

  refreshTemplates: async (params) => {
    set({ fetched: false });
    return get().fetchTemplates({ ...(params || {}), refresh: true });
  },
}));

function filterByServiceByModule(appProperties, integrationId, templates, module, fetchAllTemplates) {
  const isWhatsapp = appProperties?.installedApps?.find((app) => app?.integProps?.integId === integrationId)?.right?.service_name === "whatsapp";

  let filteredTemplates = Array.isArray(templates) ? templates : [];

  if (fetchAllTemplates) {
    return filteredTemplates;
  }

  // Filter by service type
  if (!isWhatsapp) {
    filteredTemplates = filteredTemplates.filter((template) => template?.templateType !== "whatsapp");
  }

  // Filter by module if module is provided
  if (module) {
    filteredTemplates = filteredTemplates.filter((template) => {
      const templateModule = template?.moduleName;
      // Include templates that match the module, are general templates, or have no module specified (treat as general)
      const isGeneralTemplate = !templateModule || templateModule === PLAIN_TEMPLATE_OBJECT.value;
      return templateModule === module || isGeneralTemplate;
    });
  }

  return filteredTemplates;
}
